import { lazy, Suspense } from "react";
import { Outlet } from "react-router-dom";
// auth
import { GuestGuard } from "src/auth/guard";
// layouts
import CompactLayout from "src/layouts/compact";
import AuthClassicLayout from "src/layouts/auth/classic";
// config
import { GOOGLE_CLIENT_ID } from "src/config-global";
// components
import { SplashScreen } from "src/components/loading-screen";
import { GoogleOAuthProvider } from "@react-oauth/google";

// ----------------------------------------------------------------------

//
const LoginPage = lazy(() => import("src/pages/auth/login"));
const NewPasswordPage = lazy(() => import("src/pages/auth/new-password"));
const ForgotPasswordPage = lazy(() => import("src/pages/auth/forgot-password"));
const RegisterClaims = lazy(() => import("src/pages/auth/register-claims"));
const ConfirmEmailChangePage = lazy(() => import("src/pages/auth/confirm-email-change"));
const CancelEmailChangePage = lazy(() => import("src/pages/auth/cancel-email-change"));
const LoginIdpPage = lazy(() => import("src/pages/auth/login-idp"));
const LoginIdpCallbackPage = lazy(() => import("src/pages/auth/login-idp-callback"));
const LogoutIdpPage = lazy(() => import("src/pages/auth/logout-idp"));
const IdpMobileFallbackPage = lazy(() => import("src/pages/auth/idp-mobile-fallback"));

// ----------------------------------------------------------------------

export const authRoutes = [
  {
    path: "auth",
    element: (
      <GuestGuard>
        <Suspense fallback={<SplashScreen />}>
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <Outlet />
          </GoogleOAuthProvider>
        </Suspense>
      </GuestGuard>
    ),
    children: [
      {
        path: "login",
        element: (
          <AuthClassicLayout>
            <LoginPage />
          </AuthClassicLayout>
        ),
      },
      {
        element: (
          <CompactLayout>
            <Outlet />
          </CompactLayout>
        ),
        children: [
          { path: "new-password", element: <NewPasswordPage fromProfile={false} /> },
          { path: "forgot-password", element: <ForgotPasswordPage /> },
          // External identity provider login and its redirect URL
          { path: "idp", element: <LoginIdpPage /> },
          { path: "idp/callback/:enterpriseId", element: <LoginIdpCallbackPage /> },
        ],
      },
      {
        element: (
          <CompactLayout notMax>
            <Outlet />
          </CompactLayout>
        ),
        children: [{ path: "register-claims/:enterprise", element: <RegisterClaims /> }],
      },
    ],
  },
  {
    path: "auth/confirm-email-change",
    element: (
      <Suspense fallback={<SplashScreen />}>
        <CompactLayout>
          <ConfirmEmailChangePage />
        </CompactLayout>
      </Suspense>
    ),
  },
  {
    // Logout URL of the external identity provider. Outside the GuestGuard: the IdP may send the
    // user back before the app has finished tearing down the session.
    path: "auth/idp/logout/:enterpriseId",
    element: (
      <Suspense fallback={<SplashScreen />}>
        <CompactLayout>
          <LogoutIdpPage />
        </CompactLayout>
      </Suspense>
    ),
  },
  {
    // Claimed by the mobile app (Android App Links): only rendered when the browser opens them
    path: "auth/idp/mobile",
    element: (
      <Suspense fallback={<SplashScreen />}>
        <CompactLayout>
          <Outlet />
        </CompactLayout>
      </Suspense>
    ),
    children: [
      { path: "callback/:enterpriseId", element: <IdpMobileFallbackPage variant="callback" /> },
      { path: "logout/:enterpriseId", element: <IdpMobileFallbackPage variant="logout" /> },
    ],
  },
  {
    path: "auth/cancel-email-change",
    element: (
      <Suspense fallback={<SplashScreen />}>
        <CompactLayout>
          <CancelEmailChangePage />
        </CompactLayout>
      </Suspense>
    ),
  },
];
